import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  ConnectedUsersService,
  ConnectionHistoryRecord,
  ConnectionHistoryParams,
} from '../../services/connected-users.service';
import { ConnectionDetailDialogComponent } from './connection-detail-dialog/connection-detail-dialog.component';

interface Tab {
  label: string;
  tipoId: string;
}

@Component({
  selector: 'app-connection-history',
  templateUrl: './connection-history.component.html',
  styleUrls: ['./connection-history.component.sass'],
})
export class ConnectionHistoryComponent implements OnInit, OnDestroy {
  records: ConnectionHistoryRecord[] = [];
  pagination = { page: 1, limit: 20, total: 0, totalPages: 0 };

  filterForm: FormGroup;
  loading = false;
  errorMessage = '';
  expandedCardId: number | null = null;

  private destroy$ = new Subject<void>();

  pageSizeOptions = [10, 20, 50, 100];
  Math = Math;

  tabs: Tab[] = [
    { label: 'Todos', tipoId: '' },
    { label: 'Venezuela', tipoId: 'VE' },
    { label: 'Chile', tipoId: 'CL' },
    { label: 'Operadores', tipoId: 'OPERATOR' },
  ];
  activeTab = '';

  constructor(
    private connectedUsersService: ConnectedUsersService,
    private fb: FormBuilder,
    private dialog: MatDialog,
  ) {
    this.filterForm = this.fb.group({
      userId: [''],
      startDate: [''],
      endDate: [''],
    });
  }

  ngOnInit(): void {
    this.loadHistory();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadHistory(): void {
    this.loading = true;
    this.errorMessage = '';

    const params: ConnectionHistoryParams = {
      page: this.pagination.page,
      limit: this.pagination.limit,
    };

    if (this.activeTab === 'OPERATOR') {
      params.isOperator = true;
    } else if (this.activeTab) {
      params.tipoId = this.activeTab;
    }

    const formValue = this.filterForm.value;
    if (formValue.userId && formValue.userId.trim()) {
      params.userId = formValue.userId.trim();
    }
    if (formValue.startDate) {
      const date = new Date(formValue.startDate);
      params.dateFrom = date.toISOString().split('T')[0];
    }
    if (formValue.endDate) {
      const date = new Date(formValue.endDate);
      params.dateTo = date.toISOString().split('T')[0];
    }

    this.connectedUsersService
      .getHistory(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.records = response.data || [];
          this.pagination.total = response.meta?.total || 0;
          this.pagination.page = response.meta?.page || 1;
          this.pagination.totalPages = response.meta?.totalPages || 0;
          this.loading = false;
        },
        error: (error) => {
          this.errorMessage = error.message || 'Error al cargar historial de conexiones';
          this.loading = false;
        },
      });
  }

  onTabChange(tab: Tab): void {
    this.activeTab = tab.tipoId;
    this.pagination.page = 1;
    this.loadHistory();
  }

  applyFilters(): void {
    this.pagination.page = 1;
    this.loadHistory();
  }

  clearFilters(): void {
    this.filterForm.reset();
    this.pagination.page = 1;
    this.loadHistory();
  }

  onPageSizeChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.pagination.limit = parseInt(target.value, 10);
    this.pagination.page = 1;
    this.loadHistory();
  }

  onPaginatorChange(event: any): void {
    this.pagination.page = event.pageIndex + 1;
    this.pagination.limit = event.pageSize;
    this.loadHistory();
  }

  viewDetail(record: ConnectionHistoryRecord): void {
    const isMobile = window.innerWidth < 768;
    this.dialog.open(ConnectionDetailDialogComponent, {
      data: record,
      maxHeight: '90vh',
      maxWidth: isMobile ? '95vw' : '826px',
      minWidth: isMobile ? '90vw' : '800px',
      width: isMobile ? '95vw' : undefined,
    });
  }

  toggleCard(idx: number): void {
    this.expandedCardId = this.expandedCardId === idx ? null : idx;
  }

  formatDate(dateString: string): string {
    if (!dateString) return '-';
    const parts = dateString.split('T')[0].split('-');
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }

  formatTime(dateString: string): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('es-VE', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  getUserTypeBadge(record: ConnectionHistoryRecord): { label: string; color: string } {
    if (record.isOperator) return { label: 'Operador', color: '#7c3aed' };
    if (record.tipoId === 'VE') return { label: 'Venezuela', color: '#0369a1' };
    return { label: 'Chile', color: '#b45309' };
  }

  getDeviceIcon(deviceType: string): string {
    if (deviceType === 'mobile') return 'phone_android';
    if (deviceType === 'tablet') return 'tablet';
    return 'computer';
  }
}
